import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import {
  catchError,
  EMPTY,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ICRoundService } from 'src/app/image-capture/ic-round.service';
import { AppStateService } from 'src/app/services/app-state.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { FeatureFlags } from 'src/app/services/configuration.service.model';
import { ICInstrumentGroupService } from 'src/app/services/image-capture/ic-instrument-group.service';
import { ICInstrument, ICInstrumentGroup } from 'src/app/services/image-capture/ic.service.model';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  ElasticResponseItem,
  Experiment,
  ICCassetteInfo,
  ICExperiment,
  ICExperimentStatus,
  ICImageAnalysisStatusResponse,
  ICImageStatus,
  ICInstrumentOrder,
  ICInstrumentOrderReplacement,
  ICLegendData,
  ICLegendDataIteration,
  ICLegendInstruction,
} from 'src/app/services/labpartner.service.model';
import {
  EVENT_IC_UPDATE_EXPERIMENT_INSTRUCTIONS_AND_INSTRUMENTS,
  EVENT_IC_UPDATE_EXPERIMENT_LEGENDFILE,
  EVENT_IC_UPDATE_EXPERIMENT_TEMPLATE_FILES,
} from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { NotesService } from 'src/app/services/notes-service.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import * as XLSX from 'xlsx';
import {
  ICAdjustLegendComponent,
  ICAdjustLegendComponentData,
} from '../adjust-legend/ic-adjust-legend.component';
import {
  ICSyncDeviceComponent,
  ICSyncDeviceComponentData,
} from '../sync-device/ic-sync-device.component';
import {
  ReplaceInstrumentComponent,
  ReplaceInstrumentComponentData,
} from './replace-instrument/replace-instrument.component';

@Component({
  selector: 'app-experiment-detail',
  templateUrl: './ic-experiment-detail.component.html',
  styleUrls: ['./ic-experiment-detail.component.scss'],
  standalone: false,
})
export class ICExperimentDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  selectedIndex: number = 0;

  currentExperiment: ICExperiment | null = null;

  experimentId: number = 0;
  instrumentGroups: ICInstrumentGroup[] = [];

  provideChannelData: boolean = false;

  incubation: number = 1;
  delay: number = 1;
  numberOfImages: number = 1;

  selectedInstruments: ICInstrument[] = [];

  instrumentOrders: ICInstrumentOrder[] = [];

  customSerialNumber: string = '';

  lutFiles: string[] = [];

  legendFileName: string = '';
  legendFile: File | null = null;

  templateFileNames: string[] = [];
  templateFiles: File[] = [];

  instruments: ICInstrument[] = [];

  legendData: ICLegendData[] = [];

  legendInstructions: ICLegendInstruction[] = [];
  sequenceSavedForCurrentRound: boolean = false;

  legendDataIterations: ICLegendDataIteration[] = [];
  allLegendDataIterations: ICLegendDataIteration[] = [];

  currentImages: number = 0;
  totalImages: number = 0;
  interval: NodeJS.Timeout | null = null;
  processingInterval: NodeJS.Timeout | null = null;

  successImagesProcessed: number = 0;
  errorImagesProcessed: number = 0;
  totalImagesProcessed: number = 0;
  inProgressImagesProcessed: number = 0;
  excludedImagesProcessed: number = 0;

  get showImageAnalysisResults(): boolean {
    return this.imageAnalysisResponse
      ? this.isImageAnalysisCompleted(this.imageAnalysisResponse)
      : false;
  }

  get totalImageSetsToAnalyze(): number {
    return Math.max(this.totalImagesProcessed - this.excludedImagesProcessed, 0);
  }

  get displayedAnalyzedImageSetCount(): number {
    return Math.min(this.totalProcessedImageSetCount, this.totalImageSetsToAnalyze);
  }

  get imageAnalysisProgressPercentage(): number {
    if (this.totalImageSetsToAnalyze <= 0) {
      return 0;
    }

    return (this.displayedAnalyzedImageSetCount * 100) / this.totalImageSetsToAnalyze;
  }

  get excludedImagesLegend(): string {
    if (this.excludedImagesProcessed <= 0) {
      return '';
    }

    const noun = this.excludedImagesProcessed === 1 ? 'image was' : 'images were';
    return `${this.excludedImagesProcessed} ${noun} set as excluded, therefore they will not be analyzed`;
  }

  get totalProcessedImageSetCount(): number {
    return this.successImagesProcessed + this.errorImagesProcessed;
  }

  readonly imageAnalysisResultColumns: string[] = ['status', 'numberOfImageSets'];
  imageAnalysisResultRows: { status: string; numberOfImageSets: number }[] = [];

  lastUpdateTime: Date = new Date();

  DEFAULT_UPDATE_INTERVAL: number = 10000; // Default update interval in milliseconds

  testResults: ElasticResponseItem[] = [];

  fusionLink: string = '';

  cassetteInfo: ICCassetteInfo[] = [];

  currentRoles: string[] = [];

  private readonly _destroying$ = new Subject<void>();

  imageAnalysisResponse: ICImageAnalysisStatusResponse | null = null;

  featureFlags$: Observable<FeatureFlags | null>;

  @ViewChild('firstTab', { read: ElementRef }) firstTab!: ElementRef;

  constructor(
    public service: ExperimentService,
    public accountService: UserAccountService,
    public apiService: LabpartnerService,
    public notesService: NotesService,
    private route: ActivatedRoute,
    private appState: AppStateService,
    private loggingService: LoggingService,
    private instrumentGroupService: ICInstrumentGroupService,
    private notificationService: NotificationService,
    private matDialogService: MatDialog,
    private dialog: MatDialog,
    private configService: ConfigurationService,
    private dialogService: DialogService,
    public icRoundService: ICRoundService,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
    this.updateImageAnalysisResultRows();
  }

  private updateImageAnalysisResultRows(): void {
    this.imageAnalysisResultRows = [
      {
        status: 'Successfully processed',
        numberOfImageSets: this.successImagesProcessed,
      },
      {
        status: 'Failed processed',
        numberOfImageSets: this.errorImagesProcessed,
      },
      {
        status: 'Total processed',
        numberOfImageSets: this.totalProcessedImageSetCount,
      },
    ];
  }

  protected ngOnDestroyInternal(): void {
    if (this.interval) {
      clearInterval(this.interval as any);
    }
    if (this.processingInterval) {
      clearInterval(this.processingInterval as any);
    }
  }

  loadData(setRound: boolean = false): Observable<any> {
    const obs = forkJoin([
      this.apiService.getICExperiment(this.experimentId),
      this.instrumentGroupService.getInstrumentGroups(),
    ]);
    this.subscription.add(
      obs.pipe(takeUntil(this._destroying$)).subscribe(([experiment, instrumentGroups]) => {
        this.fusionLink = this.configService.geticFusionLink(experiment.experimentId);

        this.templateFiles = []; // Reset the template files
        this.legendFile = null; // Reset the legend file
        this.currentExperiment = experiment;
        
        // Debug: Log instrument orders and replacements from backend
        console.log('[loadData] instrumentOrders:', experiment.instrumentOrders);
        if (experiment.instrumentOrders && experiment.instrumentOrders.length > 0) {
          experiment.instrumentOrders.forEach((io, index) => {
            console.log(`[loadData] InstrumentOrder ${index}:`, io);
            console.log(`[loadData] InstrumentOrder ${index} - replacements:`, io.replacements);
            if (io.replacements && io.replacements.length > 0) {
              io.replacements.forEach((r, rIndex) => {
                console.log(`[loadData] InstrumentOrder ${index} - Replacement ${rIndex}:`, {
                  instrumentId: r.instrumentId,
                  instrumentSerial: r.instrumentSerial,
                  roundNumber: r.roundNumber,
                  dateCreated: r.dateCreated,
                  instrumentOrderReplacementId: r.instrumentOrderReplacementId
                });
              });
            }
          });
        }
        
        this.legendData = JSON.parse(
          JSON.stringify(this.currentExperiment.legendData),
        ) as ICLegendData[];
        // Find if any legend data has channel data
        this.provideChannelData = this.legendData.some(
          (data: ICLegendData) => data.legendChannelData && data.legendChannelData.length > 0,
        );

        console.log('legendData', this.legendData);
        this.templateFileNames =
          this.currentExperiment.templateFiles?.map(file => file.fileName) ?? [];
        this.legendFileName = this.currentExperiment.legendFile?.fileName ?? '';
        this.incubation = experiment.incubation!;
        this.delay = experiment.delay!;
        this.numberOfImages = experiment.numberOfImages!;
        this.appState.SetCurrentExperiment(experiment as Experiment);
        this.instrumentGroups = instrumentGroups;
        this.cassetteInfo = this.currentExperiment?.cassetteInfo ?? [];

        if (setRound) {
          this.icRoundService.setRoundInformation(
            experiment.roundNumber || 1,
            experiment.roundNumber || 1,
          );
          this.setRoundInformation();
          this.subscription.add(
            this.icRoundService.currentRound$.pipe(takeUntil(this._destroying$)).subscribe(() => {
              // Update round information which rebuilds selectedInstruments array
              this.setRoundInformation();
              
              // Refresh test results to update progress bar with correct round data
              this.getTestResults(true);
              
              setTimeout(() => {
                this.firstTab.nativeElement.click();
              });
            }),
          );
        }

        this.setRoundInformation();
      }),
    );
    return obs;
  }

  setLUTFiles() {
    this.instrumentGroups.forEach((group: ICInstrumentGroup) => {
      group.instruments.forEach((instrument: ICInstrument) => {
        instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
      });
    });
    this.selectedInstruments.forEach((instrument: ICInstrument) => {
      instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
    });
  }

  ngOnInit() {
    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });

    this.experimentId = this.route.snapshot.params['id'] as number;

    this.loadData(true);
  }

  validateLegendFile(ref: any) {
    if (
      this.legendInstructions &&
      this.legendInstructions.length > 0 &&
      this.legendInstructions[0].legendDataId
    ) {
      this.dialogService
        .openConfirmDialog(
          'The sequence order is going to be deleted because another legend file is being uploaded. Would you like to continue?',
          { isOk: false, isV2: true, title: 'Replace LegendFile', width: '400px' },
        )
        .afterClosed()
        .subscribe((result: any) => {
          if (!result) {
            this.notificationService.warn('LegendFile upload cancelled.');
            return;
          }
          ref.click();
        });
    } else {
      ref.click();
    }
  }

  onLegendFileUpload(event: any) {
    const file = event.target.files[0];

    // Validate the file type before processing
    if (
      !file ||
      file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      this.notificationService.error('Please upload a valid XLSX file.');
      event.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      /** ---------------- VALIDATE FIRST SHEET (LegendData) ---------------- **/
      const firstSheetName = workbook.SheetNames[0];
      const firstSheet = workbook.Sheets[firstSheetName];
      const firstSheetData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      // Ensure the sheet has at least one row of data
      if (!firstSheetData || firstSheetData.length < 2) {
        this.notificationService.error('The first sheet must contain at least one record.');
        event.target.value = null;
        return;
      }

      // Check required columns
      const firstSheetHeaders = firstSheetData[0] as string[];
      if (!firstSheetHeaders.includes('LegendId') || !firstSheetHeaders.includes('Name')) {
        this.notificationService.error(
          'The first sheet must contain "LegendId" and "Name" columns.',
        );
        event.target.value = null;
        return;
      }

      const legendCustomHeaders = firstSheetHeaders.slice(2); // Columns after LegendId and Name
      const legendData: any[] = [];

      // Iterate over each row starting from the second (skip headers)
      for (let i = 1; i < firstSheetData.length; i++) {
        const rowNumber = i + 1; // Excel rows start at 1
        const row: any = firstSheetData[i];

        // Stop processing if the entire row is empty
        if (row.every((cell: any) => cell == null || cell.toString().trim() === '')) {
          event.target.value = null;
          break;
        }

        const [legendId, name] = row;

        // Validate required fields
        if (legendId == null || legendId === '') {
          this.notificationService.error(`Row ${rowNumber}: "LegendId" is required.`);
          event.target.value = null;
          return; // Stop processing on first error
        }

        if (!name || name.toString().trim() === '') {
          this.notificationService.error(`Row ${rowNumber}: "Name" cannot be empty.`);
          event.target.value = null;
          return; // Stop processing on first error
        }

        // Map the remaining columns into the ImportData JSON object
        const importData: any = {};
        legendCustomHeaders.forEach((header: string, idx: number) => {
          importData[header] = row[idx + 2] ?? null;
        });

        legendData.push({
          legendId,
          name: name.toString().trim(),
          importData: JSON.stringify(importData),
        });
      }

      /** ---------------- VALIDATE SECOND SHEET (ChannelData) ---------------- **/
      let channelData: any[] = [];
      if (this.provideChannelData) {
        const secondSheetName = workbook.SheetNames[1];

        // Check that the sheet exists and is named correctly
        if (!secondSheetName || secondSheetName.toLowerCase() !== 'channeldata') {
          this.notificationService.error(
            'The second sheet must be named "ChannelData" (case insensitive).',
          );
          event.target.value = null;
          return;
        }

        const secondSheet = workbook.Sheets[secondSheetName];
        const secondSheetData = XLSX.utils.sheet_to_json(secondSheet, { header: 1 });

        // Ensure the sheet has at least one row of data
        if (!secondSheetData || secondSheetData.length < 2) {
          this.notificationService.error(
            'The "ChannelData" sheet must contain at least one record.',
          );
          event.target.value = null;
          return;
        }

        // Check required columns
        const secondSheetHeaders = secondSheetData[0] as string[];
        if (
          !secondSheetHeaders.includes('LegendId') ||
          !secondSheetHeaders.includes('Channel') ||
          !secondSheetHeaders.includes('Region')
        ) {
          this.notificationService.error(
            'The "ChannelData" sheet must contain "LegendId", "Channel" and "Region" columns.',
          );
          event.target.value = null;
          return;
        }

        const channelCustomHeaders = secondSheetHeaders.slice(3);

        // Helpers for composite key normalization and duplicate tracking
        const normalizeKey = (legendId: any, channel: any, region: any) => {
          const lid = (legendId ?? '').toString().trim();
          const ch = (channel ?? '').toString().trim().toLowerCase();
          const rg = (region ?? '').toString().trim().toLowerCase();
          return `${lid}||${ch}||${rg}`;
        };
        const seen = new Map<string, number>(); // key -> first row number where it was seen
        const duplicates: Array<{ key: string; firstRow: number; dupRow: number }> = [];

        // Iterate over each row starting from the second (skip headers)
        for (let i = 1; i < secondSheetData.length; i++) {
          const rowNumber = i + 1;
          const row = secondSheetData[i] as string[];

          // Stop processing if the entire row is empty
          if (row.every((cell: any) => cell == null || cell.toString().trim() === '')) {
            event.target.value = null;
            break;
          }

          let [legendId, channel, region] = row;

          // Validate required fields
          if (legendId == null || legendId === '') {
            this.notificationService.error(`ChannelData Row ${rowNumber}: "LegendId" is required.`);
            event.target.value = null;
            return; // Stop processing on first error
          }

          if (!channel || channel.toString().trim() === '') {
            this.notificationService.error(
              `ChannelData Row ${rowNumber}: "Channel" cannot be empty.`,
            );
            event.target.value = null;
            return; // Stop processing on first error
          }

          if (!region || region.toString().trim() === '') {
            this.notificationService.error(
              `ChannelData Row ${rowNumber}: "Region" cannot be empty.`,
            );
            event.target.value = null;
            return; // Stop processing on first error
          }

          // Detect duplicates on (LegendId, Channel, Region)
          const key = normalizeKey(legendId, channel, region);
          if (seen.has(key)) {
            duplicates.push({ key, firstRow: seen.get(key)!, dupRow: rowNumber });
          } else {
            seen.set(key, rowNumber);
          }

          // Map the remaining columns into the ImportChannelData JSON object
          const importData: any = {};
          channelCustomHeaders.forEach((header: string, idx: number) => {
            importData[header] = row[idx + 3] ?? null;
          });

          channelData.push({
            legendId,
            channel: channel.toString().trim(),
            region: region.toString().trim(),
            importChannelData: JSON.stringify(importData),
          });
        }

        // If duplicates found, show warning and DO NOT persist state (allow re-upload)
        if (duplicates.length > 0) {
          const numDuplicatesToShow = Math.min(3, duplicates.length);
          const sample = duplicates
            .slice(0, numDuplicatesToShow)
            .map(d => {
              const [lid, ch, rg] = d.key.split('||');
              return `Rows ${d.firstRow} & ${d.dupRow} -> LegendId=${lid}, Channel=${ch}, Region=${rg}`;
            })
            .join('\n');

          // If your notification service does not have 'warn', replace with 'error'
          this.notificationService.warn(
            `ChannelData contains duplicate (LegendId, Channel, Region) combinations.\n` +
            `Please fix and re-upload.\n\nExamples:\n${sample}` +
            (duplicates.length > numDuplicatesToShow
              ? `\n...and ${duplicates.length - numDuplicatesToShow} more.`
              : ''),
          );

          event.target.value = null; // allow the user to re-upload
          return;
        }
      }

      /** ---------------- STORE DATA ---------------- **/
      // Assign validated legend data
      this.legendData = legendData;

      // Link channel data to each legend if applicable
      this.legendData.forEach((data: ICLegendData) => {
        data.legendChannelData = [];
        if (this.provideChannelData) {
          data.legendChannelData = (channelData || []).filter(cd => cd.legendId === data.legendId);
        }
      });

      // Store file information for upload
      this.legendFileName = file.name;
      this.legendFile = file;
      event.target.value = null;

      // Notify the user that the file is fully validated
      this.notificationService.success('Legend file validated successfully and ready for upload.');
    };

    // Handle file reading errors
    reader.onerror = () => {
      this.notificationService.error('Error reading the Legend file.');
      event.target.value = null;
    };

    // Read the XLSX file as an array buffer
    reader.readAsArrayBuffer(file);
  }

  onTemplateFilesUpload(event: any) {
    this.templateFileNames = []; // Reset the template file names
    const files: File[] = Array.from(event.target.files);

    if (files.length !== 3) {
      this.notificationService.error('Please select exactly 3 files.');
      return;
    }

    const jsonFile = files.find(file => file.type === 'application/json');
    const rawFiles = files.filter(file => file.name.endsWith('.raw'));

    if (!jsonFile) {
      this.notificationService.error('A JSON file is required.');
      return;
    }

    if (rawFiles.length !== 2) {
      this.notificationService.error('Exactly 2 RAW files are required.');
      return;
    }

    this.templateFileNames = files.map(file => file.name);
    this.templateFiles = files; // Store the file references
    this.notificationService.success('Template files loaded successfully.');
    event.target.value = null; // Reset the file input value
  }

  onSelectGroup(group: ICInstrumentGroup) {
    this.notificationService.info(`${group.name} Instrument Group Added`);
    group.instruments.forEach((instrument: ICInstrument) => {
      this.onSelectItem(instrument, false);
    });
  }

  onSelectItem(instrument: ICInstrument, showNotification: boolean = true) {
    if (showNotification) {
      this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
    }
    const instrumentIndex = this.selectedInstruments.findIndex(
      i => i.instrumentId == instrument.instrumentId,
    );
    if (instrumentIndex == -1) {
      this.selectedInstruments.push(instrument);
    }
  }

  onRemoveSelectedItem(instrument: ICInstrument) {
    this.removeInstrument(instrument);
  }

  onRemoveSelectedGroup(group: ICInstrumentGroup) {
    const groupSerials = new Set(group.instruments.map(i => i.instrumentSerial));
    const previousCount = this.selectedInstruments.length;

    this.selectedInstruments = this.selectedInstruments.filter(
      instrument => !groupSerials.has(instrument.instrumentSerial),
    );

    const removedCount = previousCount - this.selectedInstruments.length;
    if (removedCount > 0) {
      this.notificationService.info(`${group.name} Instrument Group Removed (${removedCount})`);
    }
  }

  onAddInstrument() {
    // Check if the custom serial number is empty
    if (this.customSerialNumber == '') {
      this.notificationService.warn('Please enter a serial number');
      return;
    }

    // Check if the instrument already exists in the selected instruments
    const existingInstrument = this.selectedInstruments.find(
      i => i.instrumentSerial === this.customSerialNumber,
    );
    if (existingInstrument) {
      this.notificationService.warn(`${this.customSerialNumber} Instrument Already Added`);
      this.customSerialNumber = ''; // Clear the custom serial number after adding
      return;
    }

    // Check if the custom serial number is already in use
    for (const group of this.instrumentGroups) {
      const foundInstrument = group.instruments.find(
        i => i.instrumentSerial === this.customSerialNumber,
      );
      if (foundInstrument) {
        this.onSelectItem(foundInstrument);
        this.customSerialNumber = ''; // Clear the custom serial number after adding
        return;
      }
    }

    this.subscription.add(
      this.apiService.getOrAddInstrumentBySerial(this.customSerialNumber).pipe(
        takeUntil(this._destroying$),
      ).subscribe(
        (instrument: ICInstrument) => {
          this.selectedInstruments.push(instrument);
          this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
          this.customSerialNumber = '';
        },
        () => {
          this.notificationService.warn(`Instrument does not exist`);
          this.customSerialNumber = '';
        },
      ),
    );
    // Uncomment the following code if you want to allow adding a custom serial number
    // let instrument: ICInstrument = {
    //     instrumentId: 0,
    //     instrumentSerial: this.customSerialNumber,
    //     hasLUTFile: this.lutFiles.includes(this.customSerialNumber)
    // };
    // this.selectedInstruments.push(instrument);
    // this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
    // this.customSerialNumber = ''; // Clear the custom serial number after adding
  }

  removeInstrument(instrument: any) {
    this.notificationService.info(`${instrument.instrumentSerial} Instrument Removed`);
    const instrumentIndex = this.selectedInstruments.findIndex(
      i =>
        i.instrumentId == instrument.instrumentId &&
        i.instrumentSerial == instrument.instrumentSerial,
    );
    if (instrumentIndex != -1) {
      this.selectedInstruments.splice(instrumentIndex, 1);
    }
  }

  syncDevice(instrument: any) {
    this.subscription.add(
      this.featureFlags$.pipe(takeUntil(this._destroying$)).subscribe(flags => {
        // Check if the feature flag for device sync is enabled
        if (flags?.EnableImageCaptureRequestDeviceSync) {
          this.subscription.add(
            this.apiService.icSyncDevice(instrument.instrumentSerial).pipe(
              takeUntil(this._destroying$),
            ).subscribe(
              () => {
                const dialogConfig: MatDialogConfig<ICSyncDeviceComponentData> = {
                  disableClose: false,
                  autoFocus: true,
                  width: '60%',
                  data: {
                    instrumentSerial: instrument.instrumentSerial,
                  },
                };
                const dialogRef = this.matDialogService.open<
                  ICSyncDeviceComponent,
                  ICSyncDeviceComponentData,
                  string[]
                >(ICSyncDeviceComponent, dialogConfig);
                this.subscription.add(
                  dialogRef.afterClosed().pipe(takeUntil(this._destroying$)).subscribe((dialogResult: string[] | undefined) => {
                    if (!dialogResult || dialogResult.length === 0) {
                      return;
                    }
                    this.lutFiles = dialogResult;
                    this.setLUTFiles();
                  }),
                );
              },
              () => {
                this.notificationService.error('Error syncing device. Please try again later.');
              },
            ),
          );
        } else {
          const link = `<a target="_blank" href='${this.configService.getConfig().lutFileFormURl}'>form</a>`;
          this.subscription.add(
            this.dialogService
              .openConfirmDialog(
                `The LUT (Look Up Table) file for this instrument does not exist in the cloud. The Assay Tools Team needs to sync them manually for the time being, please fill out this ${link} to request the LUT file upload for this instrument.
              `,
                { isOk: true, isV2: true, title: 'LUT', width: '800px' },
              )
              .afterClosed()
              .pipe(takeUntil(this._destroying$))
              .subscribe(() => { }),
          );
        }
      }),
    );
  }

  save() {
    if (this.isSaveDisabled() || this.currentExperiment == null) {
      this.notificationService.warn('Please fill all required fields before saving.');
      return;
    }

    const currentRound = Number(this.icRoundService.getCurrentRound());
    const currentExperiment = JSON.parse(JSON.stringify(this.currentExperiment)) as ICExperiment;
    currentExperiment.incubation = this.incubation;
    currentExperiment.delay = this.delay;
    currentExperiment.numberOfImages = this.numberOfImages;
    currentExperiment.legendData = this.legendData;

    currentExperiment.instrumentOrders = this.selectedInstruments.map((instrument, index) => {
      return {
        instrumentId: instrument.instrumentId || 0,
        order: index + 1,
        roundNumber: currentRound,
      };
    });

    // Compare current and new instrument orders
    const currentInstrumentOrders = this.getInstrumentOrdersForRound(currentRound);
    const newInstrumentOrders = currentExperiment.instrumentOrders || [];

    const isInstrumentOrdersDifferent =
      currentInstrumentOrders.length !== newInstrumentOrders.length ||
      currentInstrumentOrders.some(
        (order, index) => order.instrumentId !== newInstrumentOrders[index].instrumentId,
      );

    // Check if the current experiment is the same as the new one
    if (
      this.currentExperiment.incubation == this.incubation &&
      this.currentExperiment.delay == this.delay &&
      this.currentExperiment.numberOfImages == this.numberOfImages &&
      currentInstrumentOrders.length == this.selectedInstruments.length &&
      !isInstrumentOrdersDifferent &&
      this.legendFile == null &&
      this.templateFiles.length == 0
    ) {
      this.notificationService.warn('No changes detected. Please make changes before saving.');
      return;
    }

    // Create a FormData object for the template files
    const templateFilesFormData = new FormData();
    if (this.templateFiles.length > 0) {
      templateFilesFormData.append('experimentId', this.experimentId.toString());
      templateFilesFormData.append(
        'icExperimentId',
        currentExperiment.icExperimentId?.toString() ?? '',
      );
      this.templateFiles.forEach(file => {
        templateFilesFormData.append('templateFiles', file, file.name);
      });

      const props: { [key: string]: number } = {
        ExperimentId: this.currentExperiment?.experimentId,
        ICExperimentId: this.currentExperiment?.icExperimentId
          ? this.currentExperiment?.icExperimentId
          : 0,
        TemplateFiles: this.templateFiles.length,
        ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
      };
      this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_TEMPLATE_FILES, props);
    }

    // Create a FormData object for the legend file
    const legendFileFormData = new FormData();
    if (this.legendFile) {
      legendFileFormData.append('experimentId', this.experimentId.toString());
      legendFileFormData.append(
        'icExperimentId',
        currentExperiment.icExperimentId?.toString() ?? '',
      );
      legendFileFormData.append('legendFile', this.legendFile, this.legendFileName);

      const props: { [key: string]: number } = {
        ExperimentId: this.currentExperiment?.experimentId,
        ICExperimentId: this.currentExperiment?.icExperimentId
          ? this.currentExperiment?.icExperimentId
          : 0,
        LegendDataCount: this.legendData.length,
        ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
      };
      this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_LEGENDFILE, props);
    }

    const props: { [key: string]: number } = {
      ExperimentId: this.currentExperiment?.experimentId,
      ICExperimentId: this.currentExperiment?.icExperimentId
        ? this.currentExperiment?.icExperimentId
        : 0,
      Incubation: this.incubation,
      Delay: this.delay,
      NumberOfImages: this.numberOfImages,
      InstrumentOrdersCount: this.selectedInstruments.length,
      ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
    };
    this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_INSTRUCTIONS_AND_INSTRUMENTS, props);

    this.subscription.add(
      forkJoin([
        this.apiService.updateICExperimentInputs(this.experimentId, currentExperiment, '', {}),
        this.templateFiles.length > 0
          ? this.apiService.uploadTemplateFiles(templateFilesFormData)
          : of(null),
        this.legendFile ? this.apiService.uploadLegendFile(legendFileFormData) : of(null),
      ]).subscribe(
        () => {
          this.loadData();
          this.notificationService.success('Experiment saved successfully.');
        },
        () => {
          this.notificationService.error('Error saving experiment. Please try again later.');
        },
      ),
    );
  }

  isSaveDisabled() {
    return (
      this.incubation < 0 ||
      (this.incubation > 0 && this.incubation < 10) ||
      (!this.incubation && this.incubation !== 0) ||
      this.delay < 0 ||
      (!this.delay && this.delay !== 0) ||
      this.numberOfImages < 0 ||
      (!this.numberOfImages && this.numberOfImages !== 0) ||
      this.selectedInstruments.some(i => i.hasLUTFile == false) ||
      this.selectedInstruments.some(i => i.hasFactoryCalibrationFile == false) ||
      this.selectedInstruments.length == 0 ||
      !this.legendFileName ||
      this.templateFileNames.length != 3
    );
  }

  parseExperiment(): Experiment {
    return this.currentExperiment as Experiment;
  }

  removeTemplateFiles() {
    this.templateFiles = [];
    this.templateFileNames = [];
    this.notificationService.info('Template files removed successfully.');
  }

  removeLegendFile() {
    if (
      this.legendInstructions &&
      this.legendInstructions.length > 0 &&
      this.legendInstructions[0].legendDataId
    ) {
      // Check if the legend file is already loaded
      this.dialogService
        .openConfirmDialog(
          'The sequence order is going to be deleted because you are removing the legend file, would you like to continue?',
          { isOk: false, isV2: true, title: 'Remove LegendFile', width: '400px' },
        )
        .afterClosed()
        .subscribe((result: any) => {
          if (!result) {
            this.notificationService.warn('Legend file removal cancelled.');
            return;
          } else {
            this.legendFile = null;
            this.legendFileName = '';
            this.notificationService.info('Legend file removed successfully.');
          }
        });
    } else {
      this.legendFile = null;
      this.legendFileName = '';
      this.notificationService.info('Legend file removed successfully.');
    }
  }

  onChangeSetReady(event: any) {
    console.log('onChangeSetReady', event);
    if (this.currentExperiment) {
      this.loadData().subscribe(() => {
        this.selectedIndex = 1;
      });
    }
  }

  onChangeSequenceSaved() {
    if (this.currentExperiment) {
      this.loadData().subscribe(() => { });
    }
  }

  areAllImagesAnalyzed(): boolean {
    return (this.currentImages * 100) / this.totalImages >= 100;
  }

  onTabChange(tabIndex: any) {
    // Clear any existing intervals before starting new ones
    if (this.interval != null) {
      clearInterval(this.interval as any);
      this.interval = null;
    }
    if (this.processingInterval != null) {
      clearInterval(this.processingInterval as any);
      this.processingInterval = null;
    }

    if (tabIndex.index == 1) {
      console.log('checkInProgress');
      // Fetch test results for current round (filtering happens inside getTestResults)
      this.getTestResults(false);

      if (this.interval == null) {
        this.interval = setInterval(() => {
          this.getTestResults(true);
        }, this.DEFAULT_UPDATE_INTERVAL);
      }
    }

    if (tabIndex.index == 2) {
      this.getImageAnalysis(false);
      if (this.processingInterval == null) {
        this.processingInterval = setInterval(() => {
          this.getImageAnalysis(true);
        }, this.DEFAULT_UPDATE_INTERVAL);
      }
    }
  }

  analyzeImages() {
    const hasNonExcludedImages = this.hasNonExcludedImagesFromReplacedInstruments();
    console.log('[analyzeImages] hasNonExcludedImagesFromReplacedInstruments returned:', hasNonExcludedImages);
    
    if (hasNonExcludedImages) {
      this.dialogService
        .openConfirmDialog(
          'There are images sets sent from instruments replaced, these will be sent to be analyzed, what do you want to continue?',
          { isOk: false, isV2: true, title: 'Warning', width: '400px' },
        )
        .afterClosed()
        .subscribe((result: any) => {
          if (result) {
            this.openAdjustLegendDialog();
          }
        });
    } else {
      this.openAdjustLegendDialog();
    }
  }

  hasNonExcludedImagesFromReplacedInstruments(): boolean {
    const currentRound = this.icRoundService.getCurrentRound();
    
    console.log('[hasNonExcludedImagesFromReplacedInstruments] Starting check for round:', currentRound);
    
    // 1. Identify currently active instrument serials for the current round
    const currentSerials = new Set<string>();
    this.selectedInstruments.forEach(i => {
      if (i.instrumentSerial) currentSerials.add(i.instrumentSerial);
    });
    console.log('[hasNonExcludedImagesFromReplacedInstruments] Current active serials:', Array.from(currentSerials));

    // 2. Identify replaced instrument serials that are NOT active in the current round
    const replacedSerials = new Set<string>();
    this.instrumentOrders.forEach(order => {
      // Get the active instrument for current round
      const activeInstrument = this.getActiveInstrumentForRound(order, currentRound);
      
      // Check replacements - only those NOT active in current round
      if (order.replacements) {
        order.replacements.forEach(r => {
          const serial = r.instrumentSerial;
          if (serial && serial !== activeInstrument.instrumentSerial) {
            replacedSerials.add(serial);
          }
        });
      }
      // Check original instrument if it's not the active one
      if (order.originalInstrumentSerial && 
          order.originalInstrumentSerial !== activeInstrument.instrumentSerial) {
        replacedSerials.add(order.originalInstrumentSerial);
      }
    });

    console.log('[hasNonExcludedImagesFromReplacedInstruments] Replaced serials:', Array.from(replacedSerials));

    if (replacedSerials.size === 0) {
      console.log('[hasNonExcludedImagesFromReplacedInstruments] No replaced serials found, returning false');
      return false;
    }

    console.log('[hasNonExcludedImagesFromReplacedInstruments] cassetteInfo array:', this.cassetteInfo);
    console.log('[hasNonExcludedImagesFromReplacedInstruments] cassetteInfo details:', this.cassetteInfo.map(c => ({
      cassette: c.casseteNumber,
      serial: c.instrumentSerial,
      isExcluded: c.isExcluded,
      icExperimentId: c.icExperimentId
    })));

    // 3. Iterate through test results ONLY for the current round to find matches
    const currentRoundResults = this.testResults.filter(
      result => result.imageCapture.roundNumber === currentRound
    );
    
    console.log('[hasNonExcludedImagesFromReplacedInstruments] Test results for current round:', currentRoundResults.length);
    
    for (const result of currentRoundResults) {
      const serial = this.getResultInstrumentSerial(result) ?? undefined;

      if (serial && replacedSerials.has(serial)) {
        console.log('[hasNonExcludedImagesFromReplacedInstruments] Found result from replaced instrument:', serial, 'cassette:', result.imageCapture.casseteNumber);
        
        // 4. Check exclusion status
        // Source of truth is CassetteInfo
        // Extract base cassette number (before version suffix like .1, .2)
        const baseCassette = result.imageCapture.casseteNumber.split('.')[0];
        
        console.log('[hasNonExcludedImagesFromReplacedInstruments] Looking for baseCassette:', baseCassette, 'serial:', serial);
        
        // Match by BOTH cassette number AND instrument serial
        // This ensures we only check exclusion for the specific instrument's images
        const cassetteInfo = this.cassetteInfo.find(c => {
          if (!c.casseteNumber) return false;
          const cBaseCassette = c.casseteNumber.split('.')[0];
          const cassetteMatch = cBaseCassette === baseCassette;
          const serialMatch = c.instrumentSerial === serial;
          console.log('[hasNonExcludedImagesFromReplacedInstruments] Comparing:', {
            cCassette: c.casseteNumber,
            cBaseCassette,
            baseCassette,
            cassetteMatch,
            cInstrumentSerial: c.instrumentSerial,
            serial,
            serialMatch,
            isExcluded: c.isExcluded,
            overall: cassetteMatch && serialMatch
          });
          return cassetteMatch && serialMatch;
        });

        console.log('[hasNonExcludedImagesFromReplacedInstruments] CassetteInfo found:', cassetteInfo);
        console.log('[hasNonExcludedImagesFromReplacedInstruments] result.imageCapture.isExcluded:', result.imageCapture.isExcluded);

        let isExcluded = false;
        if (cassetteInfo) {
          // If we found a specific info record, use its status
          isExcluded = !!cassetteInfo.isExcluded;
        } else {
          // Fallback to imageCapture status if no override record exists
          isExcluded = !!result.imageCapture.isExcluded;
        }

        console.log('[hasNonExcludedImagesFromReplacedInstruments] Final isExcluded value:', isExcluded);

        if (!isExcluded) {
          console.log('[hasNonExcludedImagesFromReplacedInstruments] Found non-excluded image from replaced instrument! Returning true');
          return true;
        }
      }
    }

    console.log('[hasNonExcludedImagesFromReplacedInstruments] All images from replaced instruments are excluded, returning false');
    return false;
  }

  openAdjustLegendDialog() {
    this.testResults.forEach((result: ElasticResponseItem) => {
      result.isExtra = false; // Reset isExtra for all results
    });

    this.dialog
      .open<ICAdjustLegendComponent, ICAdjustLegendComponentData, ElasticResponseItem[]>(
        ICAdjustLegendComponent,
        {
          width: '90vw',
          maxWidth: '90vw',
          height: '90vh',
          maxHeight: '90vh',
          panelClass: 'large-viewport-dialog',
          data: {
            legendData: JSON.parse(JSON.stringify(this.legendData)),
            testResults: JSON.parse(JSON.stringify(this.testResults)),
            legendDataIteration: JSON.parse(JSON.stringify(this.allLegendDataIterations)),
            cassetteInfo: JSON.parse(JSON.stringify(this.cassetteInfo)),
            icExperimentId: this.currentExperiment?.icExperimentId || 0,
            experiment: this.currentExperiment!,
          },
        },
      )
      .afterClosed()
      .subscribe((result?: ElasticResponseItem[]) => {
        if (result) {
          this.subscription.add(
            this.apiService
              .setICExperimentAnalyzingImages(
                this.currentExperiment?.icExperimentId!,
                result.filter(r => !r.cassetteInfo?.isExcluded).map(r => r.testResultId),
                this.icRoundService.getCurrentRound(),
              )
              .subscribe(() => {
                this.subscription.add(
                  this.loadData().subscribe(() => {
                    this.notificationService.success('Experiment set to analyzing images.');
                    this.selectedIndex = 2;
                  }),
                );
              }),
          );
        } else {
          this.notificationService.warn(':: Cancelled analyzing images ::');
        }
      });
  }



  updateTime() {
    this.lastUpdateTime = new Date();
  }

  private getCompletedImageSetCount(response: ICImageAnalysisStatusResponse): number {
    return (response.successCount || 0) + (response.errorCount || 0) + (response.excludedCount || 0);
  }

  private getCassetteBaseNumber(cassetteNumber?: string | null): string | null {
    const normalizedCassetteNumber = cassetteNumber?.trim();
    if (!normalizedCassetteNumber) {
      return null;
    }

    const [baseCassetteNumber] = normalizedCassetteNumber.split('.');
    return baseCassetteNumber || null;
  }

  private isPrimaryCassetteUpload(cassetteNumber?: string | null): boolean {
    const normalizedCassetteNumber = cassetteNumber?.trim();
    if (!normalizedCassetteNumber) {
      return false;
    }

    const parts = normalizedCassetteNumber.split('.');
    return parts.length === 1 || parts[1] === '1';
  }

  private getResultImageSetKey(
    result: ElasticResponseItem,
    cassetteNumber?: string | null,
  ): string {
    const normalizedCassetteNumber =
      cassetteNumber?.trim() || result.imageCapture?.casseteNumber?.trim() || '';

    return (
      result.testResultId?.trim() ||
      [
        normalizedCassetteNumber,
        result.imageCapture?.roundNumber ?? '',
        result.imageCapture?.iterationOrder ?? '',
        result.convertedImages?.imagePathCc ?? '',
        result.convertedImages?.imagePathDark ?? '',
        result.convertedImages?.imagePathLight ?? '',
      ].join('|')
    );
  }

  private getUniqueUploadedImageSetCount(
    results: ElasticResponseItem[],
    expectedImageSetsByCassetteBase: Map<string, number>,
  ): number {
    const uploadedPrimaryImageSets = new Map<string, Set<string>>();

    results.forEach(result => {
      const cassetteNumber = result.imageCapture?.casseteNumber?.trim();
      const baseCassetteNumber = this.getCassetteBaseNumber(cassetteNumber);
      if (!cassetteNumber || !baseCassetteNumber || !this.isPrimaryCassetteUpload(cassetteNumber)) {
        return;
      }

      const existingImageSets =
        uploadedPrimaryImageSets.get(baseCassetteNumber) || new Set<string>();
      existingImageSets.add(this.getResultImageSetKey(result, cassetteNumber));
      uploadedPrimaryImageSets.set(baseCassetteNumber, existingImageSets);
    });

    return Array.from(uploadedPrimaryImageSets.entries()).reduce((sum, [baseCassetteNumber, imageSets]) => {
      const expectedImageSets = expectedImageSetsByCassetteBase.get(baseCassetteNumber);
      if (!expectedImageSets || expectedImageSets <= 0) {
        return sum + imageSets.size;
      }

      return sum + Math.min(imageSets.size, expectedImageSets);
    }, 0);
  }

  private getSlotUploadCounts(results: ElasticResponseItem[]): {
    uploadedImageSets: number;
    extraImageSets: number;
  } {
    const primaryResultIds = new Set<string>();
    const extraResultIds = new Set<string>();

    results.forEach(result => {
      const cassetteNumber = result.imageCapture?.casseteNumber?.trim();
      const baseCassetteNumber = this.getCassetteBaseNumber(cassetteNumber);
      if (!cassetteNumber || !baseCassetteNumber) {
        return;
      }

      const resultKey = this.getResultImageSetKey(result, cassetteNumber);

      if (this.isPrimaryCassetteUpload(cassetteNumber)) {
        primaryResultIds.add(resultKey);
        return;
      }

      extraResultIds.add(resultKey);
    });

    return {
      uploadedImageSets: primaryResultIds.size,
      extraImageSets: extraResultIds.size,
    };
  }

  private isImageAnalysisCompleted(response: ICImageAnalysisStatusResponse): boolean {
    if (response.status === ICImageStatus.Completed) {
      return true;
    }

    const totalCount = response.totalCount || 0;
    const inProgressCount = response.inProgressCount || 0;
    const completedCount = this.getCompletedImageSetCount(response);

    return totalCount > 0 && inProgressCount === 0 && completedCount >= totalCount;
  }

  getImageAnalysis(hideLoader: boolean) {
    this.subscription.add(
      this.apiService
        .getImageAnalysisStatus(
          this.currentExperiment!.experimentId,
          this.icRoundService.getCurrentRound(),
          hideLoader,
        )
        .subscribe({
          next: response => {
            this.imageAnalysisResponse = response;
            this.lastUpdateTime = new Date();

            this.successImagesProcessed = response.successCount || 0;
            this.errorImagesProcessed = response.errorCount || 0;
            this.totalImagesProcessed = response.totalCount || 0;
            this.inProgressImagesProcessed = response.inProgressCount || 0;
            this.excludedImagesProcessed = response.excludedCount || 0;
            this.updateImageAnalysisResultRows();

            const analysisCompleted = this.isImageAnalysisCompleted(response);

            if (analysisCompleted) {
              // If the experiment is in the last round and is in progress, set it to complete
              if (
                this.icRoundService.getIsInLastRound() &&
                this.currentExperiment?.status == ICExperimentStatus.AnalyzingImages
              ) {
                this.subscription.add(
                  this.apiService
                    .setICExperimentComplete(this.currentExperiment.icExperimentId!)
                    .subscribe(() => this.loadData()),
                );
              }

              // If all images are processed and the status is completed stop the polling
              if (response.inProgressCount == 0) {
                if (this.processingInterval) {
                  clearInterval(this.processingInterval as any);
                  this.processingInterval = null;
                }
              }
            }
          },
          error: err => {
            console.error('Error on initial image analysis status check:', err);
            // Still start polling even if the initial call fails
          },
        }),
    );
  }
  getTestResults(hideLoader: boolean) {
    this.updateTime();
    
    console.log('[getTestResults] Starting - hideLoader:', hideLoader);
    console.log('[getTestResults] Current round:', this.icRoundService.getCurrentRound());
    console.log('[getTestResults] selectedInstruments count:', this.selectedInstruments.length);
    console.log('[getTestResults] instrumentOrders count:', this.instrumentOrders.length);
    
    this.subscription.add(
      // Fetch the test results for the current experiment
      this.apiService.getICTestResults(this.currentExperiment?.experimentId!, hideLoader).pipe(
        takeUntil(this._destroying$),
      ).subscribe(
        response => {
          let currentTestResults: ElasticResponseItem[] = [];
          this.currentImages = 0;
          
          console.log('[getTestResults] API response count:', response?.length || 0);
          
          if (response && response.length > 0) {
            this.testResults = response;
            currentTestResults = this.testResults.filter(
              (result: ElasticResponseItem) =>
                result.imageCapture.roundNumber == this.icRoundService.getCurrentRound(),
            );
            
            console.log('[getTestResults] Filtered results for current round:', currentTestResults.length);
          }
          // If there are no test results, return early
          if (currentTestResults.length > 0) {
            // If the current experiment is not in progress, set it to in progress
            if (
              this.icRoundService.getIsInLastRound() &&
              this.currentExperiment?.status == ICExperimentStatus.Ready
            ) {
              this.subscription.add(
                this.apiService
                  .setICExperimentInProgress(this.currentExperiment?.icExperimentId!)
                  .subscribe(
                    () => {
                      this.currentExperiment!.status = ICExperimentStatus.InProgress;
                      this.currentExperiment = JSON.parse(
                        JSON.stringify(this.currentExperiment),
                      ) as ICExperiment;
                    },
                    () => {
                      this.notificationService.error('Error setting experiment as InProgress.');
                    },
                  ),
              );
            }

            // Reset the current images count
            this.currentImages = 0;

            const expectedImageSetsByCassetteBase = this.legendDataIterations.reduce(
              (acc, iteration) => {
                const baseCassetteNumber = this.getCassetteBaseNumber(iteration.casseteNumber);
                if (!baseCassetteNumber) {
                  return acc;
                }

                acc.set(
                  baseCassetteNumber,
                  (acc.get(baseCassetteNumber) || 0) + iteration.numberOfImageSets,
                );
                return acc;
              },
              new Map<string, number>(),
            );

            const totalUploadedImageSets = this.getUniqueUploadedImageSetCount(
              currentTestResults,
              expectedImageSetsByCassetteBase,
            );

            // Update the current images for each selected instrument based on the response
            this.selectedInstruments.forEach((instrument: ICInstrument, slotIndex: number) => {
              console.log(`[getTestResults] Processing instrument ${slotIndex}:`, instrument.instrumentSerial);
              
              if (instrument.currentImages != null && instrument.totalImages != null) {
                if (!instrument.instrumentId) {
                  console.log(`[getTestResults] No active instrumentId found for slot ${slotIndex}`);
                  return;
                }

                // LegendDataIteration rows are rewritten to the active instrument whenever a
                // replacement happens in the round, so slot ownership must be derived from the
                // active instrument only. Including historical instrument ids can pull in another
                // slot's iterations when the same serial appears elsewhere in the experiment.
                const currentRound = this.icRoundService.getCurrentRound();
                const slotCassetteNumbers = new Set<string>();
                
                console.log(`[getTestResults] Slot ${slotIndex} - Looking for round ${currentRound}, active instrumentId:`, instrument.instrumentId);
                
                const matchingIterations = this.legendDataIterations.filter(iteration =>
                  iteration.instrumentId === instrument.instrumentId
                );
                const expectedImageSetsBySlotCassetteBase = matchingIterations.reduce(
                  (acc, iteration) => {
                    const baseCassetteNumber = this.getCassetteBaseNumber(iteration.casseteNumber);
                    if (!baseCassetteNumber) {
                      return acc;
                    }

                    acc.set(
                      baseCassetteNumber,
                      (acc.get(baseCassetteNumber) || 0) + iteration.numberOfImageSets,
                    );
                    return acc;
                  },
                  new Map<string, number>(),
                );
                const expectedImageSetsForSlot = Array.from(
                  expectedImageSetsBySlotCassetteBase.values(),
                ).reduce((sum, imageSetCount) => sum + imageSetCount, 0);
                
                console.log(`[getTestResults] Slot ${slotIndex} - Found ${matchingIterations.length} matching iterations`);
                if (matchingIterations.length > 0) {
                  console.log('[getTestResults] First iteration:', matchingIterations[0]);
                }
                
                matchingIterations.forEach(iteration => {
                  slotCassetteNumbers.add(iteration.casseteNumber);
                });
                  
                console.log(`[getTestResults] Slot ${slotIndex} - cassettes for round ${currentRound}:`, slotCassetteNumbers.size);

                const belongsToInstrument = (result: ElasticResponseItem, instrumentSerial: string): boolean => {
                  const resolvedSerial = this.getResultInstrumentSerial(result);
                  return resolvedSerial === instrumentSerial;
                };

                // 1. Calculate for Active Instrument - filter by instrument serial AND cassette belongs to this slot
                const activeResults = currentTestResults.filter(
                  (result: ElasticResponseItem) => {
                    const cassetteBase = this.getCassetteBaseNumber(result.imageCapture.casseteNumber);
                    return belongsToInstrument(result, instrument.instrumentSerial) &&
                      !!cassetteBase &&
                      slotCassetteNumbers.has(cassetteBase);
                  }
                );

                const activeUploadCounts = this.getSlotUploadCounts(activeResults);

                instrument.currentImages = Math.min(
                  activeUploadCounts.uploadedImageSets,
                  expectedImageSetsForSlot,
                );
                instrument.currentExtraImages = Math.max(
                  activeUploadCounts.uploadedImageSets - expectedImageSetsForSlot,
                  0,
                ) + activeUploadCounts.extraImageSets;

                // 2. Calculate for History Instruments - filter by instrument serial AND cassette belongs to this slot
                if (instrument.history && instrument.history.length > 0) {
                  instrument.history.forEach(replacedInst => {
                    const replacedResults = currentTestResults.filter(
                      (result: ElasticResponseItem) => {
                        const cassetteBase = this.getCassetteBaseNumber(result.imageCapture.casseteNumber);
                        return belongsToInstrument(result, replacedInst.instrumentSerial) &&
                          !!cassetteBase &&
                          slotCassetteNumbers.has(cassetteBase);
                      }
                    );

                    const replacedUploadCounts = this.getSlotUploadCounts(replacedResults);
                    replacedInst.currentImages = Math.min(
                      replacedUploadCounts.uploadedImageSets,
                      expectedImageSetsForSlot,
                    );
                    replacedInst.currentExtraImages = Math.max(
                      replacedUploadCounts.uploadedImageSets - expectedImageSetsForSlot,
                      0,
                    ) + replacedUploadCounts.extraImageSets;
                  });
                }
              }
            });

            this.currentImages = Math.min(totalUploadedImageSets, this.totalImages);
          }
        },
        () => {
          if (!hideLoader) {
            this.notificationService.error(
              'Error retrieving test results. Please try again later.',
            );
          }
        },
      ),
    );
  }

  adustLegend() {
    this.testResults.forEach((result: ElasticResponseItem) => {
      result.isExtra = false; // Reset isExtra for all results
    });

    this.subscription.add(
      this.dialog
        .open<ICAdjustLegendComponent, ICAdjustLegendComponentData, void>(ICAdjustLegendComponent, {
          width: '90vw',
          maxWidth: '90vw',
          height: '90vh',
          maxHeight: '90vh',
          panelClass: 'large-viewport-dialog',
          data: {
            legendData: JSON.parse(JSON.stringify(this.legendData)),
            testResults: JSON.parse(JSON.stringify(this.testResults)),
            legendDataIteration: JSON.parse(JSON.stringify(this.allLegendDataIterations)),
            cassetteInfo: JSON.parse(JSON.stringify(this.cassetteInfo)),
            icExperimentId: this.currentExperiment?.icExperimentId || 0,
            experiment: this.currentExperiment!,
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.apiService
            .getICExperiment(this.experimentId)
            .subscribe((experiment: ICExperiment) => {
              if (experiment) {
                this.cassetteInfo = experiment.cassetteInfo || [];
              }
            });
        }),
    );
  }

  replaceInstrument(instrumentOrder: ICInstrumentOrder) {
    console.log('[replaceInstrument] instrumentOrder:', instrumentOrder);
    console.log('[replaceInstrument] originalInstrumentSerial:', instrumentOrder.originalInstrumentSerial);
    console.log('[replaceInstrument] instrumentSerial:', instrumentOrder.instrumentSerial);
    console.log('[replaceInstrument] originalInstrumentId:', instrumentOrder.originalInstrumentId);
    console.log('[replaceInstrument] instrumentId:', instrumentOrder.instrumentId);
    console.log('[replaceInstrument] replacements:', instrumentOrder.replacements);

    const selectedInstrumentOrderId = instrumentOrder.instrumentOrderId;

    const localOrder =
      this.instrumentOrders.find(io => io.instrumentOrderId === selectedInstrumentOrderId) ||
      instrumentOrder;
    const currentRound = this.icRoundService.getCurrentRound();
    const serial = this.getActiveInstrumentSerialForRound(localOrder, currentRound);

    console.log('[replaceInstrument] Resolved serial for dialog (CURRENT instrument):', serial);

    if (!serial) {
      return;
    }

    this.openReplaceInstrumentDialog(localOrder, currentRound, serial, this.testResults);
  }

  private openReplaceInstrumentDialog(
    instrumentOrder: ICInstrumentOrder,
    currentRound: number,
    serial: string,
    sourceResults: ElasticResponseItem[],
  ) {

    this.subscription.add(
      this.dialog
        .open<ReplaceInstrumentComponent, ReplaceInstrumentComponentData, any>(
          ReplaceInstrumentComponent,
          {
            width: '80%',
            data: {
              instrumentGroups: JSON.parse(JSON.stringify(this.instrumentGroups)),
              selectedInstruments: JSON.parse(
                JSON.stringify(
                  ([] as any).map((i: any) => {
                    const instrument = this.instruments.find(
                      ins => ins.instrumentId == i.instrumentId,
                    );
                    return {
                      instrumentId: i.instrumentId,
                      instrumentSerial: instrument?.instrumentSerial,
                      hasLUTFile: this.lutFiles.includes(instrument?.instrumentSerial || ''),
                      hasFactoryCalibrationFile: instrument?.hasFactoryCalibrationFile || false,
                      currentImages: 0,
                      totalImages: this.legendDataIterations
                        .filter(iteration => iteration.instrumentId == i.instrumentId)
                        .reduce((sum, iteration) => sum + iteration.numberOfImageSets, 0),
                    } as ICInstrument;
                  }),
                ),
              ),
              lutFiles: JSON.parse(JSON.stringify(this.lutFiles)),
              currentInstrumentSerialNumber: serial,
              lastCassettes: this.getLastCassettesForReplace(currentRound, serial, sourceResults),
            },
          },
        )
        .afterClosed()
        .subscribe((result: ICInstrument[]) => {
          if (result != null) {
            const replacements: ICInstrumentOrderReplacement[] = [];
            result.forEach((instrument: ICInstrument) => {
              replacements.push({
                instrumentId: instrument.instrumentId,
                instrumentOrderId: instrumentOrder.instrumentOrderId,
              } as ICInstrumentOrderReplacement);
            });

            // Extract all testResultIds from testResults (already loaded in the view)
            const testResultIds = this.testResults
              .map(tr => tr.testResultId)
              .filter((id): id is string => !!id)
              .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

            this.subscription.add(
              this.apiService
                .icReplaceInstrument(
                  instrumentOrder.instrumentOrderId!,
                  replacements[0],
                  this.icRoundService.getCurrentRound(),
                  testResultIds
                )
                .subscribe(() => {
                  this.notificationService.success('Instrument replaced successfully.');
                  // Reload all data and wait for it to complete before reloading test results
                  this.loadData().subscribe(() => {
                    // After loadData completes, setRoundInformation has been called
                    // Use setTimeout to ensure the view updates before reloading test results
                    setTimeout(() => {
                      // Force reload of Kibana data to get updated test results
                      this.getTestResults(false);
                    }, 100);
                  });
                }),
            );
          } else {
            this.notificationService.warn(':: Cancelled replacing instrument ::');
          }
        }),
    );
  }

  getInstrumentLabel(i: number) {
    const instrument = this.instruments.find(
      ins => ins.instrumentId == this.instrumentOrders[i]?.instrumentId,
    );
    return instrument
      ? instrument.instrumentSerial
      : this.instrumentOrders[i]?.instrumentSerial || 'Unknown Instrument';
  }

  getInstrumentHistory(instrument: ICInstrumentOrder) {
    if (instrument.replacements && instrument.replacements.length > 0) {
      const originalSerial =
        this.instruments.find(ins => ins.instrumentId == instrument.originalInstrumentId)
          ?.instrumentSerial ||
        instrument.originalInstrumentSerial ||
        'Unknown';

      // Sort replacements to ensure chronological order, then remove the last one (which is the current instrument)
      const sortedReplacements = [...instrument.replacements].sort(
      (a, b) => this.getReplacementSequenceId(a) - this.getReplacementSequenceId(b),
      );

      // We want to show history, so we exclude the current active replacement
      const historyReplacements = sortedReplacements.slice(0, -1);

      if (historyReplacements.length === 0) {
        return `(Instrument History: ${originalSerial})`;
      }

      const replacementSerials = historyReplacements
        .map(
          r =>
            this.instruments.find(ins => ins.instrumentId == r.instrumentId)?.instrumentSerial ||
            r.instrumentSerial ||
            'Unknown',
        )
        .join(', ');

      return `(Instrument History: ${originalSerial}, ${replacementSerials})`;
    } else {
      return '';
    }
  }

  downloadReport() {
    if (this.imageAnalysisResponse) {
      this.subscription.add(
        this.apiService
          .downloadImageAnalysisReport(this.imageAnalysisResponse.imageAnalysisId)
          .subscribe(response => {
            if (!response.body) {
              return;
            }

            // Extract the filename from the Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            console.log(contentDisposition);
            let filename = 'image-analysis.csv'; // Fallback filename

            if (contentDisposition) {
              filename = contentDisposition;
            }

            // Create a Blob URL and trigger the download
            const url = window.URL.createObjectURL(response.body);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename; // Use the extracted filename
            a.click();
            window.URL.revokeObjectURL(url);
          }),
      );
    }
  }

  // Cancel the experiment
  cancelExperiment() {
    this.subscription.add(
      this.dialogService
        .openConfirmDialog(
          'Are you sure to cancel the experiment? If you cancel it there is no way to recover it to continue working on it?',
          { isOk: false, isV2: true, title: 'Cancel Experiment', width: '400px' },
        )
        .afterClosed()
        .pipe(
          switchMap((result: any) => {
            if (result) {
              // the user confirmed the cancellation
              return this.apiService.icCancelExperiment(this.currentExperiment!.experimentId).pipe(
                tap(() => {
                  this.notificationService.success('Experiment cancelled successfully.');
                  this.loadData();
                }),
                catchError(() => {
                  this.notificationService.error(
                    'Error cancelling experiment. Please try again later.',
                  );
                  return EMPTY;
                }),
              );
            } else {
              // the user closed the modal or clicked "No", just show a warning message
              this.notificationService.warn('Experiment cancellation aborted.');
              return EMPTY; // no API call is made
            }
          }),
        )
        .subscribe(),
    );
  }

  isLatestRoundSelected(): boolean {
    return Number(this.icRoundService.getCurrentRound()) === Number(this.icRoundService.getLastRound());
  }

  isHistoricalRoundSelected(): boolean {
    return !this.isLatestRoundSelected();
  }

  canEditCreatedRoundInputs(): boolean {
    return (
      !!this.currentExperiment &&
      this.isLatestRoundSelected() &&
      this.currentExperiment.status === ICExperimentStatus.Created &&
      !this.currentExperiment.isCancelled
    );
  }

  canEditInstrumentList(): boolean {
    return this.canEditCreatedRoundInputs();
  }

  canReplaceInstrument(): boolean {
    return (
      !!this.currentExperiment &&
      this.isLatestRoundSelected() &&
      this.sequenceSavedForCurrentRound &&
      (this.currentExperiment.status === ICExperimentStatus.Ready ||
        this.currentExperiment.status === ICExperimentStatus.InProgress) &&
      !this.currentExperiment.isCancelled
    );
  }

  private getInstrumentOrderRound(order: ICInstrumentOrder): number {
    return Number(order.roundNumber ?? 1);
  }

  private getInstrumentOrdersForRound(round: number): ICInstrumentOrder[] {
    return [...(this.currentExperiment?.instrumentOrders || [])]
      .filter(order => this.getInstrumentOrderRound(order) === Number(round))
      .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
  }

  private getLegendInstructionsForRound(round: number): ICLegendInstruction[] {
    return (this.currentExperiment?.legendInstructions || []).filter(
      instruction => Number(instruction.roundNumber ?? 1) === Number(round),
    );
  }

  private getRoundReplacements(
    order: ICInstrumentOrder,
    round: number,
  ): ICInstrumentOrderReplacement[] {
    const targetRound = this.getInstrumentOrderRound(order) || Number(round);

    return [...(order.replacements || [])]
      .filter(replacement => Number(replacement.roundNumber ?? targetRound) === targetRound)
      .sort((left, right) => this.getReplacementSequenceId(left) - this.getReplacementSequenceId(right));
  }

  private getBaseInstrumentForOrder(order: ICInstrumentOrder): {
    instrumentId: number;
    instrumentSerial: string;
  } {
    const baseInstrumentId = order.originalInstrumentId || order.instrumentId || 0;
    const baseInstrumentSerial =
      order.originalInstrumentSerial ||
      this.instruments.find(ins => ins.instrumentId == baseInstrumentId)?.instrumentSerial ||
      order.instrumentSerial ||
      this.instruments.find(ins => ins.instrumentId == order.instrumentId)?.instrumentSerial ||
      '';

    return {
      instrumentId: baseInstrumentId,
      instrumentSerial: baseInstrumentSerial,
    };
  }

  /**
   * Gets the active instrument for a given round, considering replacements.
   * InstrumentOrder rows are now round-scoped, so only same-round replacements apply.
   */
  private getActiveInstrumentForRound(order: ICInstrumentOrder, round: number): { instrumentId: number, instrumentSerial: string } {
    const applicableReplacements = this.getRoundReplacements(order, round);
    const replacement = applicableReplacements[applicableReplacements.length - 1];

    if (replacement) {
      const serial = replacement.instrumentSerial ||
        this.instruments.find(ins => ins.instrumentId == replacement.instrumentId)?.instrumentSerial ||
        '';
      return {
        instrumentId: replacement.instrumentId!,
        instrumentSerial: serial
      };
    }

    return this.getBaseInstrumentForOrder(order);
  }

  setRoundInformation() {
    const currentRound = Number(this.icRoundService.getCurrentRound());
    console.log('[setRoundInformation] Starting for round:', currentRound);

    const persistedLegendInstructions = this.getLegendInstructionsForRound(currentRound);
    this.sequenceSavedForCurrentRound = persistedLegendInstructions.some(
      instruction => !!instruction.legendDataId,
    );
    this.legendInstructions = JSON.parse(
      JSON.stringify(persistedLegendInstructions),
    ) as ICLegendInstruction[];
    this.allLegendDataIterations = JSON.parse(
      JSON.stringify(this.currentExperiment?.legendDataIterations),
    ) as ICLegendDataIteration[];
    
    console.log('[setRoundInformation] Total allLegendDataIterations:', this.allLegendDataIterations?.length || 0);
    console.log('[setRoundInformation] Iterations by round:', 
      this.allLegendDataIterations?.reduce((acc, it) => {
        acc[it.roundNumber] = (acc[it.roundNumber] || 0) + 1;
        return acc;
      }, {} as any)
    );
    
    // Log instrumentIds for each round
    const round1Iterations = this.allLegendDataIterations?.filter(it => it.roundNumber === 1) || [];
    const round2Iterations = this.allLegendDataIterations?.filter(it => it.roundNumber === 2) || [];
    console.log('[setRoundInformation] Round 1 instrumentIds:', round1Iterations.map(it => it.instrumentId));
    console.log('[setRoundInformation] Round 2 instrumentIds:', round2Iterations.map(it => it.instrumentId));
    
    this.legendDataIterations = JSON.parse(
      JSON.stringify(
        this.currentExperiment?.legendDataIterations?.filter(
          iteration => iteration.roundNumber == currentRound,
        ),
      ),
    ) as ICLegendDataIteration[];
    
    console.log('[setRoundInformation] legendDataIterations for current round:', this.legendDataIterations?.length || 0);
    this.totalImages = this.legendDataIterations.reduce(
      (sum, iteration) => sum + iteration.numberOfImageSets,
      0,
    );
    this.instrumentGroups.forEach((group: ICInstrumentGroup) => {
      group.instruments.forEach((instrument: ICInstrument) => {
        if (
          this.instruments.findIndex(i => i.instrumentSerial == instrument.instrumentSerial) == -1
        ) {
          this.instruments.push(instrument);
        }
      });
    });

    this.instrumentOrders = JSON.parse(
      JSON.stringify(this.getInstrumentOrdersForRound(currentRound)),
    ) as ICInstrumentOrder[];
    this.selectedInstruments =
      this.instrumentOrders.map(i => {
        const activeInstrument = this.getActiveInstrumentForRound(i, currentRound);
        const baseInstrument = this.getBaseInstrumentForOrder(i);

        // Slot totals must be calculated from the currently active instrument only.
        // When a replacement occurs, backend updates the round's LegendDataIteration rows to the
        // new active instrument. Including historical ids here can incorrectly absorb iterations
        // from other slots that used the same instrument in the past.
        const slotIterations = this.legendDataIterations.filter(iteration =>
          iteration.instrumentId === activeInstrument.instrumentId
        );
        const totalImagesSlot = slotIterations.reduce(
          (sum, iteration) => sum + iteration.numberOfImageSets,
          0
        );
        
        console.log(`[setRoundInformation] Slot - Round ${currentRound} - Active: ${activeInstrument.instrumentSerial} - totalImages: ${totalImagesSlot}`);

        const inst = {
          instrumentOrderId: i.instrumentOrderId || 0,
          instrumentId: activeInstrument.instrumentId,
          instrumentSerial: activeInstrument.instrumentSerial,
          hasLUTFile:
            this.instruments.find(ins => ins.instrumentId == activeInstrument.instrumentId)?.hasLUTFile ||
            i.hasLUTFile,
          hasFactoryCalibrationFile:
            this.instruments.find(ins => ins.instrumentId == activeInstrument.instrumentId)?.hasFactoryCalibrationFile ||
            i.hasFactoryCalibrationFile,
          currentImages: 0,
          totalImages: totalImagesSlot,
          replacements: i.replacements,
          originalInstrumentId: i.originalInstrumentId || 0,
          originalInstrumentSerial: i.originalInstrumentSerial || '',
          history: [],
        } as ICInstrument;

        // Build history: show all replacements in chronological order
        const history: ICInstrument[] = [];

        console.log(`[setRoundInformation] Round ${currentRound} - i.replacements:`, i.replacements);
        console.log(`[setRoundInformation] Round ${currentRound} - filtering for roundNumber === ${currentRound}`);

        const replacementsInCurrentRound = this.getRoundReplacements(i, currentRound);

        console.log(`[setRoundInformation] Round ${currentRound} - replacementsInCurrentRound:`, replacementsInCurrentRound);

        console.log(`[setRoundInformation] Round ${currentRound} - instrumentBeforeReplacement:`, baseInstrument);
        console.log(`[setRoundInformation] Round ${currentRound} - activeInstrument:`, activeInstrument);
        console.log(`[setRoundInformation] Round ${currentRound} - replacementsInCurrentRound: ${replacementsInCurrentRound.length}`);

        // Add the base round instrument whenever there was at least one replacement in this round.
        if (replacementsInCurrentRound.length > 0) {
          console.log(`[setRoundInformation] Adding replaced instrument to history: ${baseInstrument.instrumentSerial}`);
          history.push({
            instrumentId: baseInstrument.instrumentId,
            instrumentSerial: baseInstrument.instrumentSerial,
            currentImages: 0,
            currentExtraImages: 0,
            totalImages: totalImagesSlot,
            hasLUTFile: false,
            replacementDate:
              replacementsInCurrentRound[0].dateCreated ||
              replacementsInCurrentRound[0].dateUpdated ||
              i.dateUpdated ||
              i.dateCreated,
            roundNumber: undefined,
          } as any);
        }

        replacementsInCurrentRound.slice(0, -1).forEach(r => {
          if (r.instrumentId) {
            const rSer =
              r.instrumentSerial ||
              this.instruments.find(ins => ins.instrumentId == r.instrumentId)?.instrumentSerial ||
              'Unknown';

            console.log(`[setRoundInformation] Adding intermediate replacement to history: ${rSer}`);
            history.push({
              instrumentId: r.instrumentId,
              instrumentSerial: rSer,
              currentImages: 0,
              currentExtraImages: 0,
              totalImages: totalImagesSlot,
              hasLUTFile: false,
              replacementDate: r.dateCreated || r.dateUpdated,
              roundNumber: r.roundNumber,
            } as any);
          }
        });

        // Reverse history so oldest is at bottom and newest is at top
        inst.history = history.reverse();
        return inst;
      });

    if (this.currentExperiment?.status == ICExperimentStatus.Complete) {
      // Avoid to assign this since we need the real values
    }
  }

    private getReplacementSequenceId(replacement: ICInstrumentOrderReplacement): number {
    return replacement.instrumentOrderReplacementId
      || replacement.instrumentReplacementId
      || 0;
    }

    private getResultInstrumentSerial(result: ElasticResponseItem): string | null {
    const serialFromImageCapture = result.imageCapture?.instrumentSerial?.trim();
    if (serialFromImageCapture) {
      return serialFromImageCapture;
    }

    const testResultId = result.testResultId?.trim();
    if (!testResultId || !testResultId.includes('-')) {
      return null;
    }

    const candidate = testResultId.split('-')[0]?.trim();
    if (!candidate || candidate.includes('_')) {
      return null;
    }

    return candidate;
    }

  private normalizeInstrumentSerial(serial?: string | null): string {
    return (serial || '').trim().toUpperCase();
  }

  private getActiveInstrumentSerialForRound(order: ICInstrumentOrder, round: number): string {
    return this.getActiveInstrumentForRound(order, round).instrumentSerial;
  }

  private getLastCassettesForReplace(
    currentRound: number,
    currentSerial: string,
    sourceResults: ElasticResponseItem[],
  ): string[] {
    const normalizedCurrentSerial = this.normalizeInstrumentSerial(currentSerial);
    const normalizedCurrentRound = Number(currentRound);

    let instrumentResults = sourceResults.filter(r => {
      const resultRound = Number(r.imageCapture?.roundNumber);
      if (!Number.isFinite(resultRound) || resultRound !== normalizedCurrentRound) {
        return false;
      }

      const serialFromImageCapture = this.normalizeInstrumentSerial(r.imageCapture?.instrumentSerial);

      return serialFromImageCapture === normalizedCurrentSerial;
    });

    const uniqueCassettes = Array.from(
      new Set(instrumentResults.map(r => r.imageCapture?.casseteNumber).filter(c => !!c)),
    ) as string[];

    return uniqueCassettes
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .slice(-5);
  }

  onLoadPreviousSequence() {
    this.legendInstructions = JSON.parse(
      JSON.stringify(this.getLegendInstructionsForRound(Number(this.icRoundService.getCurrentRound()) - 1)),
    ) as ICLegendInstruction[];
  }

  formatReplacementDate(date: Date | string | undefined): string {
    if (!date) return '';

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '';

      // Format as "MM/DD/YYYY HH:MM AM/PM"
      const options: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      return dateObj.toLocaleString('en-US', options);
    } catch {
      return '';
    }
  }
}
